import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";

import { ApiError, get, onSessionExpired, patch, post, setCsrfToken } from "@/shared/api/client";
import type { Registration, SessionInfo, User } from "@/shared/api/types";

type AuthValue = {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User>;
  register: (data: { email: string; password: string; display_name: string; timezone: string }) => Promise<Registration>;
  signOut: () => Promise<void>;
  updateUser: (changes: Partial<Pick<User, "display_name" | "timezone" | "theme" | "notification_preferences">>) => Promise<User>;
  setUser: (user: User | null) => void;
};

const AuthContext = createContext<AuthValue | null>(null);
const SESSION_KEY = ["session"];

export function applyTheme(theme: User["theme"] | undefined) {
  const root = document.documentElement;
  try {
    if (!theme || theme === "system") {
      delete root.dataset.theme;
      localStorage.removeItem("pl-theme");
    } else {
      root.dataset.theme = theme;
      localStorage.setItem("pl-theme", theme);
    }
  } catch {
    /* storage unavailable: the attribute alone still applies */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      try {
        const info = await get<SessionInfo>("/api/v1/auth/session");
        setCsrfToken(info.csrf_token);
        return info.user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: Infinity,
    retry: (count, error) => !(error instanceof ApiError && error.status === 401) && count < 2,
  });

  const setUser = useCallback((user: User | null) => {
    queryClient.setQueryData(SESSION_KEY, user);
    applyTheme(user?.theme);
  }, [queryClient]);

  useEffect(() => {
    applyTheme(session.data?.theme);
  }, [session.data?.theme]);

  useEffect(() => onSessionExpired(() => {
    const wasSignedIn = queryClient.getQueryData(SESSION_KEY);
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "session" });
    queryClient.setQueryData(SESSION_KEY, null);
    if (wasSignedIn) sessionStorage.setItem("pl-expired", "1");
  }), [queryClient]);

  const value = useMemo<AuthValue>(() => ({
    user: session.data ?? null,
    loading: session.isLoading,
    signIn: async (email, password) => {
      const info = await post<SessionInfo>("/api/v1/auth/login", { email, password });
      setCsrfToken(info.csrf_token);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "session" });
      setUser(info.user);
      return info.user;
    },
    register: async (data) => {
      const result = await post<Registration>("/api/v1/auth/register", data);
      // A pending account gets no session until an administrator approves it.
      if (result.status === "approved") {
        setCsrfToken(result.csrf_token);
        setUser(result.user);
      }
      return result;
    },
    signOut: async () => {
      try {
        await post("/api/v1/auth/logout");
      } finally {
        // Order matters: null the observed session first, then drop everything
        // else. Clearing the whole cache first would orphan the session observer
        // and leave the signed-out user visible.
        setCsrfToken(null);
        setUser(null);
        queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "session" });
      }
    },
    updateUser: async (changes) => {
      const user = await patch<User>("/api/v1/account", changes);
      setUser(user);
      return user;
    },
    setUser: (user) => setUser(user),
  }), [session.data, session.isLoading, queryClient, setUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}

export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}
